import { Injectable } from '@angular/core';
import {
  Address,
  BillingInformation,
  CustomFieldDetail,
  EndOrderDetails,
  EstablishmentCheckout,
  PaymentMethod,
  PersonalInformation,
  ShippingOption,
} from '@pedix-workspace/utils';
import { subHours } from 'date-fns';

export type EndOrderFormSession = {
  updated: Date;
  details: EndOrderForm;
};

export type EndOrderForm = {
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  requestBilling?: boolean;
  billingInformation?: BillingInformation | null;
  shippingOption?: ShippingOption | null;
  paymentMethod?: PaymentMethod | null;
  tableNumber?: string | null;
  howMuch?: number | null;
  address?: Address | null;
  customFieldDetailValues?: CustomFieldDetail['value'][] | null;
  couponCode?: string | null;

  // Food Bank Donation ------------------------------------------------
  bankFoodDonationChecked?: boolean;
};

@Injectable({ providedIn: 'root' })
export class EndOrderFormService {
  private readonly SESSION_STORAGE_KEY = 'PXW_ORDER_SESSION';

  getInitialData(data: {
    storedEndOrderData: Pick<EndOrderDetails, 'personalInformation' | 'billingInformation'>;
    checkoutData: EstablishmentCheckout;
  }): EndOrderForm {
    const endOrderFormSessionData = this.sessionData;

    if (endOrderFormSessionData) {
      return endOrderFormSessionData;
    }

    let address: Address = {};

    if (data.storedEndOrderData?.personalInformation?.address) {
      address = {
        ...data.storedEndOrderData.personalInformation.address,
        street: data.checkoutData.shippingOptionDelivery?.disableAddressInMap
          ? data.storedEndOrderData.personalInformation.address.street
          : null,
        addressDetails: data.checkoutData.shippingOptionDelivery?.disableAddressInMap
          ? null
          : data.storedEndOrderData.personalInformation.address.addressDetails,
      };
    }

    return {
      name: data.storedEndOrderData?.personalInformation?.name,
      phone: data.storedEndOrderData?.personalInformation?.phone,
      email: data.storedEndOrderData?.personalInformation?.email,
      requestBilling: false,
      billingInformation: data.storedEndOrderData?.billingInformation,
      address,
      customFieldDetailValues: data.checkoutData.customFields?.map(
        customField => customField.defaultValue,
      ),
    };
  }

  getInitialDataFromOrder(order: EndOrderDetails) {
    return {
      name: order.personalInformation.name,
      phone: order.personalInformation.phone,
      email: order.personalInformation.email || order.parcelTrackingEmail || null,
      requestBilling: !!order.billingInformation,
      billingInformation: order.billingInformation,
      address: order.personalInformation.address,
      customFieldDetailValues: order.customFieldDetails?.map(
        customFieldDetail => customFieldDetail.value,
      ),
      couponCode: order.couponConfigurationSnapshot?.code,
      shippingOption: order.shippingOption,
      paymentMethod: order.paymentMethod,
      tableNumber: order.tableNumber,
      howMuch: order.howMuch,
    };
  }

  set sessionData(details: EndOrderForm) {
    const endOrderFormSessionData: EndOrderFormSession = {
      updated: new Date(),
      details,
    };
    sessionStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(endOrderFormSessionData));
  }

  get sessionData(): EndOrderForm | null {
    const sessionData = sessionStorage.getItem(this.SESSION_STORAGE_KEY);

    if (sessionData) {
      const sessionData: EndOrderFormSession = JSON.parse(
        sessionStorage.getItem(this.SESSION_STORAGE_KEY) || '{}',
      );
      const updated = new Date(sessionData.updated);

      if (updated < subHours(Date.now(), 1)) {
        this.clearSessionData();
      } else {
        // NOTE: refreshes session timer
        this.sessionData = sessionData.details;

        return sessionData.details;
      }
    }
    return null;
  }

  clearSessionData() {
    sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
  }
}
